import { EApiType, ECommonDialogType } from '../data/Constants';
import type { ISearchRequest } from '../../types/IRequestTypes';
import { t } from 'i18next';
import type { Dispatch } from '@reduxjs/toolkit';
import { openDialog } from '../store/states/DialogSlice';
import { setIsDataUpdated } from '../store/states/PdmSlice';
import { AxiosResponse } from 'axios';
type ApiResponse = Partial<AxiosResponse> & Pick<AxiosResponse, 'data'>;

/**
 * Returns the response from api in paginated format
 * @param {Object} response API response with data and headers
 * @param {ISearchRequest} action Search request action
 * @returns Paginated response data
 */
export function handlePaginatedData<T>(
  response: { data: T[]; headers?: Record<string, unknown> },
  action: ISearchRequest
) {
  let total = 0;
  const raw = response.headers?.['x-pagination'];
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as { total?: number };
      total = parsed?.total ?? 0;
    } catch {
      total = 0;
    }
  }
  return {
    data: response.data || [],
    searchKey: action.searchCode,
    currentPage: action.page,
    totalRecords: total,
    recordsPerPage: action.limit,
  };
}

/**
 * To return data object on API call success
 * Handles specific dispatches based on api response types
 *
 * @param {ApiResponse} response API response object
 * @param {EApiType} action Type of API action that was performed
 * @param {Dispatch} dispatch Redux dispatch function
 * @returns {Object} Response data object
 */
export function handleSuccess(response: ApiResponse, action?: EApiType, dispatch?: Dispatch) {
  //Specific dispatches based on api responses
  if (action === EApiType.SaveProperties) {
    dispatch?.(setIsDataUpdated(false));
    dispatch?.(
      openDialog({
        type: ECommonDialogType.Success,
        data: t('messages.submit.general'),
      })
    );
  } else if (action === EApiType.SendNotification) {
    dispatch?.(
      openDialog({
        type: ECommonDialogType.Success,
        data: t('request.success'),
      })
    );
  } else if (action === EApiType.Download) {
    let fileName = response.headers?.['content-disposition'];
    const prefixRegex =
      /^(?:inline|attachment);\s*filename\*?=UTF-8''|^(?:inline|attachment);\s*filename=/i;
    fileName =
      fileName?.replace(prefixRegex, '')?.replace(/^(['"])(.*)\1$/, '$2') ?? 'downloaded_file.xlsx';

    const url = globalThis.URL.createObjectURL(
      new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
      })
    );
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    dispatch?.(
      openDialog({
        type: ECommonDialogType.Success,
        data: t('messages.downloadMessage', { filename: `"${fileName}"` }),
      })
    );
  }
  return {
    data: response.data,
  };
}
